// client/components/ui/ModalOrSheet.jsx
import React, { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Z_INDEX } from "./zIndex.js";
import { acquireBodyScrollLock } from "../../utils/bodyScrollLock.js";
import { X } from "lucide-react";
import AppButton from "./AppButton.jsx";

/** Mobile: Sheet an sichtbare Viewport-Fläche koppeln (Tastatur / iOS-Adressleiste). */
function readMobileSheetFrame() {
  if (typeof window === "undefined") return null;
  const mq = window.matchMedia("(max-width: 767px)");
  if (!mq.matches) return null;
  const vv = window.visualViewport;
  const height = vv?.height ?? window.innerHeight;
  const offsetTop = vv?.offsetTop ?? 0;
  return {
    top: offsetTop,
    height,
    panelMaxHeight: Math.max(240, Math.round(height * 0.92)),
  };
}

function useMobileSheetFrame(active) {
  const [frame, setFrame] = useState(() => (active ? readMobileSheetFrame() : null));

  useEffect(() => {
    if (!active || typeof window === "undefined") {
      setFrame(null);
      return;
    }

    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => {
      setFrame(mq.matches ? readMobileSheetFrame() : null);
    };

    update();
    const opts = { passive: true };
    mq.addEventListener("change", update);
    window.visualViewport?.addEventListener("resize", update, opts);
    window.visualViewport?.addEventListener("scroll", update, opts);
    window.addEventListener("resize", update, opts);
    return () => {
      mq.removeEventListener("change", update);
      window.visualViewport?.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      setFrame(null);
    };
  }, [active]);

  return frame;
}

/**
 * ModalOrSheet - Responsive Modal/Sheet Komponente
 * 
 * Desktop (>=md): Zentriertes Modal
 * Mobile (<md): BottomSheet mit Safe-Area-Support
 * 
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - title: string (optional)
 * - children: ReactNode
 * - footer: ReactNode (optional)
 * - footerSticky: boolean (default: true) – false = Footer scrollt mit dem Inhalt mit
 * - maxWidth: "sm" | "md" | "lg" | "xl" | "2xl" | number (px) - nur Desktop
 * - closeOnOverlay: boolean (default: true)
 * - escCloses: boolean (default: true)
 */
export default function ModalOrSheet({
  open,
  onClose,
  title,
  ariaLabel = "Dialog",
  children,
  footer,
  footerSticky = true,
  headerActions = null,
  maxWidth = "md",
  closeOnOverlay = true,
  escCloses = true
}) {
  const panelRef = useRef(null);
  const overlayMouseDownRef = useRef(false);
  const lastFocusedRef = useRef(null);
  const titleId = useId();
  const mobileFrame = useMobileSheetFrame(open);

  // Body-Scroll-Lock (referenzzählt — kompatibel mit mehreren Overlays)
  useEffect(() => {
    if (!open) return;
    const release = acquireBodyScrollLock();
    return release;
  }, [open]);

  // ESC-Handler
  useEffect(() => {
    if (!open || !escCloses) return;
    const onKey = e => {
      if (e.key === "Escape" && !e.defaultPrevented) {
        e.preventDefault();
        onClose?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, escCloses, onClose]);

  // Focus-Trap (einfach)
  useEffect(() => {
    if (!open || !panelRef.current) return;
    lastFocusedRef.current = document.activeElement;
    const root = panelRef.current;
    const focusable = root.querySelectorAll("button, a, input, textarea, select, [tabindex]:not([tabindex='-1'])");
    const list = Array.from(focusable).filter(el => !el.hasAttribute("disabled") && el.getAttribute("aria-hidden") !== "true");
    const trap = e => {
      if (e.key !== "Tab" || e.defaultPrevented) return;
      if (list.length === 0) {
        e.preventDefault();
        root.focus();
        return;
      }
      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    };
    root.addEventListener("keydown", trap);
    const alreadyInside = root.contains(document.activeElement);
    if (!alreadyInside) {
      requestAnimationFrame(() => {
        if (list.length > 0) {
          list[0]?.focus({ preventScroll: true });
        } else {
          root.focus({ preventScroll: true });
        }
      });
    }
    return () => {
      root.removeEventListener("keydown", trap);
      lastFocusedRef.current?.focus?.({ preventScroll: true });
    };
  }, [open]);

  // SSR-Guard / geschlossen
  if (typeof document === "undefined") return null;
  if (!open) return null;

  // Max-Width für Desktop
  const maxWidthValue = typeof maxWidth === "number"
    ? `${maxWidth}px`
    : maxWidth === "sm"
      ? "400px"
      : maxWidth === "md"
        ? "600px"
        : maxWidth === "lg"
          ? "800px"
          : maxWidth === "xl"
            ? "1200px"
            : maxWidth === "2xl"
              ? "1680px"
              : "600px";

  const shellStyle = mobileFrame
    ? {
        top: mobileFrame.top,
        height: mobileFrame.height,
        bottom: "auto",
      }
    : undefined;
  const panelMaxHeight = mobileFrame
    ? `${mobileFrame.panelMaxHeight}px`
    : undefined;
  const hasStickyFooter = Boolean(footer) && footerSticky;
  const panelStyle = {
    maxWidth: `min(100%, ${maxWidthValue})`,
  };
  if (mobileFrame && hasStickyFooter) {
    panelStyle.height = panelMaxHeight;
    panelStyle.maxHeight = panelMaxHeight;
  } else if (mobileFrame) {
    panelStyle.maxHeight = panelMaxHeight;
  } else {
    panelStyle.maxHeight = hasStickyFooter
      ? "calc(100dvh - 48px)"
      : "min(92dvh, calc(100dvh - env(safe-area-inset-top, 0px) - 8px))";
  }

  return createPortal(<div
    className="fixed inset-0 flex items-end md:items-center justify-center overflow-hidden p-0 md:p-6"
    style={{
      zIndex: Z_INDEX.modal,
      ...shellStyle,
    }}
    aria-modal="true"
    role="dialog"
    aria-labelledby={title ? titleId : undefined}
    aria-label={title ? undefined : ariaLabel}
    onMouseDown={e => {
    if (!closeOnOverlay) return;
    overlayMouseDownRef.current = e.currentTarget === e.target;
  }} onMouseUp={e => {
    if (!closeOnOverlay) return;
    const upOnOverlay = e.currentTarget === e.target;
    if (overlayMouseDownRef.current && upOnOverlay) {
      onClose?.();
    }
    overlayMouseDownRef.current = false;
  }}>
      {/* Dimmer */}
      <div className="absolute inset-0 overlay max-md:backdrop-blur-none md:backdrop-blur-[2px]" aria-hidden="true" />

      {/* Modal/Sheet Card */}
      <div
        ref={panelRef}
        tabIndex={-1}
        className="relative z-10 flex w-full min-h-0 flex-col overflow-hidden rounded-t-2xl border border-default bg-surface-elevated shadow-2xl md:rounded-2xl"
        style={panelStyle}
        onMouseDown={e => e.stopPropagation()}
      >
        {/* Header */}
        {title && <div className={`shrink-0 z-10 flex items-center gap-2 px-4 sm:px-6 py-3 border-b ${"border-default bg-surface-elevated"}`}>
            <div className={`md:hidden absolute left-1/2 -translate-x-1/2 top-1.5 h-1.5 w-10 rounded-full ${"bg-white/20"}`} aria-hidden="true" />
            {title && <h2 id={titleId} className={`flex-1 text-lg font-semibold truncate ${"text-primary"}`}>
                {title}
              </h2>}
            {headerActions ? (
              <div className="flex items-center gap-2 shrink-0">{headerActions}</div>
            ) : null}
            <AppButton type="button" variant="ghost" onClick={onClose} className={`${headerActions ? "" : "ml-auto "} !h-11 !w-11 !min-h-[44px] !min-w-[44px] !p-0 rounded-lg font-normal ${"text-secondary"}`} aria-label="Schließen" title="Schließen">
              <X className="w-5 h-5" />
            </AppButton>
          </div>}
        {!title && <AppButton type="button" variant="ghost" onClick={onClose} className={`absolute top-2 right-2 z-20 !h-11 !w-11 !min-h-[44px] !min-w-[44px] !p-0 rounded-lg font-normal ${"text-secondary"}`} aria-label="Schließen" title="Schließen">
            <X className="w-5 h-5" />
          </AppButton>}

        {/* Inhalt — bei footerSticky=false scrollt der Footer mit */}
        <div
          className="flex-1 min-h-0 min-w-0 overflow-y-auto overscroll-contain touch-pan-y px-4 py-4 max-md:py-3 sm:px-6 text-primary"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {children}
          {footer && !footerSticky ? (
            <div className="mt-4 pt-4 border-t border-default mobile-safe-area-bottom">
              {footer}
            </div>
          ) : null}
        </div>
        {footer && footerSticky ? (
          <div className="shrink-0 border-t border-default bg-surface-elevated px-4 py-3 sm:px-6 mobile-safe-area-bottom">
            {footer}
          </div>
        ) : null}
      </div>
    </div>, document.body);
}